import { useEffect, useLayoutEffect } from "react";
import gsap from "gsap";

// useLayoutEffect warns on the server (TanStack Start SSR), so fall back there.
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Subtle scroll-in reveal for elements marked with `[data-reveal]` inside
 * `scope`. Fades them in and slides them up once as they enter the viewport.
 *
 * Only opacity + transform are animated (never layout properties), and the
 * whole effect is skipped when the user prefers reduced motion -- in that case
 * (and with JS disabled) the elements simply stay fully visible.
 *
 * The start state is applied in a layout effect so it lands before the first
 * paint and there is no flash of content that then animates in.
 */
export function useScrollReveal(scope, options = {}) {
  const { y = 18, duration = 0.6, ease = "power2.out" } = options;

  useIsoLayoutEffect(() => {
    const root = scope.current;
    if (!root) return;

    const mm = gsap.matchMedia();
    let observer;

    mm.add(
      "(prefers-reduced-motion: no-preference)",
      () => {
        const items = gsap.utils.toArray(root.querySelectorAll("[data-reveal]"));
        if (!items.length) return;

        gsap.set(items, { autoAlpha: 0, y });

        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const el = entry.target;
              gsap.to(el, {
                autoAlpha: 1,
                y: 0,
                duration,
                ease,
                delay: Number(el.dataset.revealDelay || 0),
                overwrite: "auto",
              });
              observer.unobserve(el);
            });
          },
          { rootMargin: "0px 0px -10% 0px", threshold: 0.12 },
        );

        items.forEach((el) => observer.observe(el));
      },
      root,
    );

    return () => {
      if (observer) observer.disconnect();
      mm.revert();
    };
  }, [scope, y, duration, ease]);
}
